import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function AboutPhilosophy() {
  const headerRef = useScrollAnimation({ threshold: 0.15 });
  const pillarsRef = useScrollAnimation({ threshold: 0.15, staggerChildren: 150 });
  const quoteRef = useScrollAnimation({ threshold: 0.2 });
  const familyHeaderRef = useScrollAnimation({ threshold: 0.15 });
  const familyCardsRef = useScrollAnimation({ threshold: 0.15, staggerChildren: 100 });

  const pillars = [
    { icon: 'ri-fire-line', title: 'Discipline Over Motivation', description: 'Motivation fades. Discipline stays. True transformation happens when you show up even on the days you don\'t feel like it. That\'s where champions are built.' },
    { icon: 'ri-restaurant-line', title: 'Nutrition as Foundation', description: 'Food is fuel, not punishment. Your body is a high performance machine. Feed it right, and it will reward you with energy, strength, and results that last.' },
    { icon: 'ri-heart-pulse-line', title: 'Transformation is Total', description: 'Real change isn\'t just physical. It\'s mental, emotional, and spiritual. When you transform your body, you transform your entire life and how you show up in the world.' },
    { icon: 'ri-trophy-line', title: 'Leave a Legacy', description: 'Your transformation doesn\'t end with you. When you change your life, you inspire your family, friends, and community. That\'s the ripple effect of a true legacy.' },
  ];

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">

        {/* Section Header */}
        <div ref={headerRef} className="text-center mb-12 sm:mb-16 scroll-animate-fade-up">
          <div
            className="inline-block text-xs font-bold tracking-widest uppercase mb-4 px-4 py-2 rounded-full"
            style={{ background: 'rgba(184,134,11,0.12)', color: '#B8860B' }}
          >
            The Legacy Mindset
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5 sm:mb-6" style={{ color: '#B8860B' }}>
            The Philosophy That Changes Lives
          </h2>
          <p className="text-base sm:text-lg max-w-3xl mx-auto" style={{ color: '#555555' }}>
            This isn't just about workouts and meal plans. It's about building a mindset that transforms everything you touch.
          </p>
        </div>

        {/* 4 Core Pillars */}
        <div ref={pillarsRef} className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-14 sm:mb-20">
          {pillars.map((pillar, index) => (
            <div
              key={index}
              className="p-6 sm:p-8 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer bg-white scroll-animate-stagger"
              style={{ border: '2px solid #e0e0e0' }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#B8860B';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(184,134,11,0.12)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center rounded-full mb-5 sm:mb-6"
                style={{ background: 'linear-gradient(135deg, #B8860B, #8B0000)' }}
              >
                <i className={`${pillar.icon} text-2xl sm:text-3xl`} style={{ color: '#ffffff' }}></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4" style={{ color: '#1a1a1a' }}>
                {pillar.title}
              </h3>
              <p className="text-sm sm:text-base leading-relaxed" style={{ color: '#555555' }}>
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        {/* Philosophy Quote */}
        <div
          ref={quoteRef}
          className="relative p-8 sm:p-12 rounded-lg mb-14 sm:mb-20 scroll-animate-fade-up"
          style={{ background: '#ffffff', border: '2px solid #B8860B', boxShadow: '0 4px 24px rgba(184,134,11,0.08)' }}
        >
          <div className="absolute top-4 left-5 sm:top-8 sm:left-8 text-6xl sm:text-8xl font-serif opacity-15" style={{ color: '#8B0000' }}>
            "
          </div>
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <p className="text-lg sm:text-2xl md:text-3xl italic font-light leading-relaxed mb-6 sm:mb-8" style={{ color: '#1a1a1a' }}>
              The Legacy Mindset isn't about perfection. It's about progress. It's about showing up every single day, even when it's hard. It's about understanding that your body is capable of far more than your mind believes. And it's about building something that lasts, not just a six pack, but a life you're proud of, a family you inspire, and a legacy that outlives you.
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-10 sm:w-16" style={{ background: '#B8860B' }}></div>
              <p className="text-base sm:text-lg font-bold tracking-wide" style={{ color: '#B8860B' }}>
                BENNY LEGACY
              </p>
              <div className="h-px w-10 sm:w-16" style={{ background: '#B8860B' }}></div>
            </div>
          </div>
        </div>

        {/* Legacy Family */}
        <div className="text-center">
          <div ref={familyHeaderRef} className="scroll-animate-fade-up">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 sm:mb-6" style={{ color: '#B8860B' }}>
              Welcome to The Legacy Family
            </h3>
            <p className="text-base sm:text-lg max-w-3xl mx-auto mb-7 sm:mb-8" style={{ color: '#555555' }}>
              From Ottawa to Toronto to clients around the world, the Legacy Family is a global community of high performers, go getters, and everyday people who refuse to settle. Whether you're training in person or virtually, you're never alone on this journey.
            </p>
          </div>
          <div ref={familyCardsRef} className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 max-w-4xl mx-auto">
            {[
              { icon: 'ri-global-line', title: 'Worldwide Reach', desc: 'Virtual coaching means you can train with Benny from anywhere on the planet' },
              { icon: 'ri-user-heart-line', title: 'Personal Connection', desc: 'Every client gets direct access to Benny. No middlemen, no automated responses' },
              { icon: 'ri-team-line', title: 'Community Support', desc: 'Join a tribe of like minded individuals all chasing their own legacy' },
            ].map((item, i) => (
              <div
                key={i}
                className="p-5 sm:p-6 rounded-lg bg-white scroll-animate-stagger"
                style={{ border: '1px solid #e0e0e0' }}
              >
                <div
                  className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center rounded-full mx-auto mb-4"
                  style={{ background: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
                >
                  <i className={`${item.icon} text-xl sm:text-2xl`} style={{ color: '#B8860B' }}></i>
                </div>
                <h4 className="text-lg sm:text-xl font-bold mb-2" style={{ color: '#1a1a1a' }}>
                  {item.title}
                </h4>
                <p className="text-sm" style={{ color: '#555555' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}